import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, BookMarked, ClipboardCheck, History, NotebookPen, BarChart3, Award, Lock, FileText } from "lucide-react";
import logo from "@/assets/logo-light.png";
import { useSafeAreaTop } from "@/hooks/useSafeAreaTop";
import { useActiveCertification } from "@/hooks/useActiveCertification";
import { useCertifications } from "@/hooks/useCertifications";
import { useAccessibleCertifications } from "@/hooks/useAccessibleCertifications";
import { useAuth } from "@/contexts/AuthContext";
import SampleExamDialog from "@/components/SampleExamDialog";


const topItems = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Insights", url: "/insights", icon: BarChart3 },
];

const groups = [
  {
    label: "Practice",
    items: [
      { title: "Chapter Practice", url: "/chapters", icon: BookMarked },
      { title: "Practice Results", url: "/practice-results", icon: NotebookPen },
    ],
  },
  {
    label: "Exam Practice",
    items: [
      { title: "Mock Exams", url: "/mock-exams", icon: ClipboardCheck },
      { title: "Mock Results", url: "/mock-results", icon: History },
    ],
  },
];

const UserSidebar = () => {
  const location = useLocation();
  const safeTopFallback = useSafeAreaTop();

  // Show the user's active certification as a static label.
  const adminCerts = useCertifications();
  const { activeCertId } = useActiveCertification();
  const activeCert = adminCerts.find((c) => c.certificationId === activeCertId);

  // No active certification -> free plan state with an upgrade call to action.
  const { isAdmin, isSuperAdmin } = useAuth();
  const { accessibleIds, isLoaded } = useAccessibleCertifications();
  const isFreePlan = !isAdmin && !isSuperAdmin && isLoaded && accessibleIds.length === 0;

  return (
    <aside className="w-60 h-full min-h-[100dvh] bg-sidebar text-sidebar-foreground flex flex-col shrink-0 sticky top-0" style={{ paddingTop: `calc(env(safe-area-inset-top, 0px) + ${safeTopFallback}px)` }}>
      {/* Brand */}
      <NavLink to="/dashboard" className="block px-5 h-14 flex items-center border-b border-sidebar-border">
        <img src={logo} alt="A Software Tester" className="h-7 opacity-70 hover:opacity-100 transition-opacity" />
      </NavLink>

      {/* Active Certification (static label) */}
      {activeCert && !isFreePlan && (
        <div className="px-4 py-3 border-b border-sidebar-border bg-sidebar-accent/30">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-sidebar-primary shrink-0" />
            <p
              className="text-xs font-semibold text-sidebar-foreground truncate"
              title={activeCert.name}
            >
              {activeCert.name}
            </p>
          </div>
        </div>
      )}

      {/* Free plan state */}
      {isFreePlan && (
        <div className="px-4 py-2.5 border-b border-sidebar-border">
          <span className="text-[11px] font-medium uppercase tracking-wide text-sidebar-foreground/50">
            Free plan
          </span>
        </div>
      )}



      {/* Nav */}
      <nav className="flex-1 min-h-0 overflow-y-auto scrollbar-ultra-thin py-4 px-3 space-y-4">
        {/* Top-level items */}
        <div className="space-y-1">
          {topItems.map((item) => (
            <NavLink
              key={item.url}
              to={item.url}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-sidebar-primary text-sidebar-primary-foreground"
                    : `${isFreePlan ? "text-sidebar-foreground/45" : "text-sidebar-foreground/70"} hover:text-sidebar-foreground hover:bg-sidebar-accent`
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.title}</span>
              {isFreePlan && <Lock className="h-3.5 w-3.5 opacity-70" />}
            </NavLink>
          ))}
        </div>

        {/* Grouped sections */}
        {groups.map((group) => (
          <div key={group.label} className="space-y-1">
            <span className="px-3 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
              {group.label}
            </span>
            {group.items.map((item) => (
              <NavLink
                key={item.url}
                to={item.url}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-sidebar-primary text-sidebar-primary-foreground"
                      : `${isFreePlan ? "text-sidebar-foreground/45" : "text-sidebar-foreground/70"} hover:text-sidebar-foreground hover:bg-sidebar-accent`
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{item.title}</span>
                {isFreePlan && <Lock className="h-3.5 w-3.5 opacity-70" />}
              </NavLink>
            ))}
          </div>
        ))}

        {/* Sample exam — always opt in, never auto started */}
        {isFreePlan && (
          <div className="space-y-1">
            <span className="px-3 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
              Try it free
            </span>
            <SampleExamDialog>
              <button
                type="button"
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 text-sidebar-foreground hover:bg-sidebar-accent"
              >
                <FileText className="h-4 w-4" />
                Sample Exam
              </button>
            </SampleExamDialog>

          </div>
        )}
      </nav>


    </aside>
  );
};

export default UserSidebar;
